<template>
  <div v-if="userInfo?.user_id" class="sider-up-user-info">
    <div
      :class="{
        'coin-wrapper': checkPermission(ActionListKey.SIDEBAR_GO_EQUITY),
      }"
      @click="goLink"
    >
      <div class="flex-y-center justify-between text-14">
        <div class="flex-y-center gap-[4px] font-bold">
          {{ $t('yan-bi-sheng-yu') }}
        </div>
        <div class="flex-y-center gap-[4px] font-bold">
          {{ totalLeftCost }}
          <a-popover
            v-if="
              canBuyCredit && hasActionPermission('center_equity_buy_yanbi')
            "
          >
            <template #content>{{ $t('jia-gou-yan-bi') }}</template>
            <SvgCircleAdd
              class="xmov-icon icon-add text-primary cursor-pointer w-[14px] h-[14px]"
              @click.stop="onAddCredits"
            />
          </a-popover>
        </div>
      </div>
      <ProgressLine
        class="mt-[10px]"
        :name="$t('kong-jian-shi-yong')"
        :total="rightsInfo?.total_storage"
        :left="rightsInfo?.total_used_storage"
      >
        <template #data>
          <span>
            <span
              :class="{
                'text-error': isOverStorage < MIN_STORAGE,
                'font-bold': true,
              }"
              >{{ getFormateFileSize(rightsInfo.total_used_storage, false) }}
            </span>
            /
            <span class="text-font-60">
              {{ getFormateFileSize(rightsInfo?.total_storage, false) }}
            </span>

            <a-popover
              v-if="
                canBuyStorage &&
                hasActionPermission('center_equity_buy_storage')
              "
            >
              <template #content>{{ $t('jia-gou-kong-jian') }}</template>
              <SvgCircleAdd
                class="xmov-icon icon-add offset-top-2px text-primary cursor-pointer w-[14px] h-[14px] ml-[4px]"
                @click.stop="addStorage"
              />
            </a-popover>
          </span>
        </template>
      </ProgressLine>
    </div>
    <div v-if="checkPermission(ActionListKey.UPGRADE_BENEFITS)">
      <div
        class="up-btn shark-wrap overflow-hidden relative"
        @click="handleUpgrade"
      >
        <iconpark-icon
          class="mr-[7px]"
          name="sider-diamond"
          size="16"
        ></iconpark-icon>
        {{ btnText }}
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import ProgressLine from './progress.vue'
import { ActionListKey, checkPermission } from '~/utils/permission'
import {
  ORDER_BEHAVIOR,
  ORDER_TYPE,
  RIGHTS_FEATURE,
  USER_LEVEL_KEY,
} from '~/api/interface'
import { useAddFeature, useAddStorage } from '~/features/paymentDesk'
import { useUpgrade } from '~/hooks/useUpgrade'
import { usePermission } from '~/hooks/usePermission.ts'
import { PageKey } from '~/constants/page'

import SvgCircleAdd from '~/assets/svg/circle-add.svg?component'

const { $t } = useNuxtApp()

const userInfo = useUserInfo()

const MIN_STORAGE = 0

const { canBuyStorage, canBuyCredit } = usePermission()
const { hasActionPermission } = usePageAndActionPermissionData()

// 只在“个人”账号才显示
const btnText = computed(() => {
  if (userInfo.value?.rights_type === USER_LEVEL_KEY.TEAM_TASTE) {
    return $t('sheng-ji-quan-yi')
  }
  if (userInfo.value?.rights_type === USER_LEVEL_KEY.TEAM_LIGHT) {
    return $t('xu-fei-quan-yi')
  }

  return userInfo.value?.rights_type !== USER_LEVEL_KEY.TASTE
    ? $t('xu-fei-quan-yi')
    : $t('sheng-ji-quan-yi')
})

const handleUpgrade = () => {
  useUpgrade()
}

const rightsInfo = computed(
  () =>
    userInfo.value!.rights || {
      storage: 0,
      total_storage: 0,
    }
)

const isOverStorage = computed(
  () =>
    userInfo.value!.rights?.total_storage -
    userInfo.value!.rights?.total_used_storage
)

// 跳转到权益管理
const goLink = () => {
  if (checkPermission(ActionListKey.SIDEBAR_GO_EQUITY)) {
    const router = useRouter()
    router.push({
      name: PageKey.CENTER_EQUITY,
    })
  }
}

// 加购存储空间
const addStorage = () => {
  useAddStorage({
    title: $t('jia-gou-cun-chu-kong-jian'),
    user_info: userInfo.value,
    behavior: ORDER_BEHAVIOR.ADD_PURCHASE,
    feature_type: RIGHTS_FEATURE.storage,
    order_type: ORDER_TYPE.STORAGE,
  })
}

// 加购言币
const onAddCredits = () => {
  if (userInfo.value?.is_expired) {
    return
  }

  useAddFeature({
    title: $t('jia-gou-yan-bi'),
    feature_type: RIGHTS_FEATURE.credit_coin,
    order_type: ORDER_TYPE.CREDITS,
    user_info: userInfo.value,
    behavior: ORDER_BEHAVIOR.ADD_PURCHASE,
  })
}

// 账号剩余言币
const totalLeftCost = computed(() => userInfo.value?.rights?.credit_coin)

const upgradeBtnBg = computed(
  () =>
    `url(${getFeUrl(
      '/youyan-platform/png/btn-up-bg-1.png?x-oss-process=image/format,webp'
    )})`
)
</script>

<style lang="scss" scoped>
.offset-top-2px {
  transform: translateY(2px);
}
.icon-add {
  opacity: 0.8;
  &:hover {
    opacity: 1;
  }
}
</style>

<style lang="scss">
.sider-up-user-info {
  @apply px-[16px] mb-[20px];
  .coin-wrapper {
    @apply cursor-pointer -m-[15px] p-[15px];
    &:hover {
      box-shadow: 0px 1px 8px 0px rgba(4, 14, 48, 0.1);
    }
  }
  .up-btn {
    @apply h-[40px] w-full text-[14px] font-medium mt-[20px] flex items-center justify-center transition-all;
    font-size: 14px;
    color: #ffffff;
    font-weight: 600;
    background-size: cover;
    border: none;
    cursor: pointer;
    border-radius: 14px;

    &:hover {
      background: linear-gradient(313deg, #b216ff 0%, #ff6d96 100%);
      color: #ffffff;
    }
  }
}

.light {
  .up-btn {
    background: v-bind('upgradeBtnBg') no-repeat;
    background-size: cover;
    color: #ffffff;

    &:hover {
      background: linear-gradient(313deg, #b216ff 0%, #ff6d96 100%);
      color: #ffffff;
    }
  }
}

.dark {
  .up-btn {
    background: linear-gradient(313deg, #b216ff 0%, #ff6d96 100%);
    color: #ffffff;
    &:hover {
      background: v-bind('upgradeBtnBg') no-repeat;
      background-size: 100% 100%;
      color: #ffffff;
    }
  }
}
</style>
